import React, { useMemo, useRef, useEffect } from 'react';
import type { Reservation, ReservableTable, ReservationSettings } from '../../types/reservation.types';

/**
 * Floor-by-time timeline (Gantt): one row per table, time along the x-axis,
 * each reservation drawn as a block spanning its sitting. Lets staff see the
 * whole service at a glance — which tables are free at 7:00 vs 7:30 — instead
 * of reading a list. Tap a block to open its detail.
 */

interface Props {
  reservations: Reservation[];
  tables: ReservableTable[];
  settings: ReservationSettings | null;
  selectedDate: string; // ISO yyyy-mm-dd
  onViewReservation: (r: Reservation) => void;
}

const PX_PER_MIN = 2.4;
const ROW_H = 44;
const LABEL_W = 116;

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  PENDING: { bg: '#E8A13C', text: '#3A2A12' },
  CONFIRMED: { bg: 'var(--action, #A66A10)', text: '#fff' },
  SEATED: { bg: '#4E9E77', text: '#fff' },
  COMPLETED: { bg: '#9CA3AF', text: '#fff' },
  NO_SHOW: { bg: '#B3261E', text: '#fff' },
  CANCELLED: { bg: '#D1D5DB', text: '#4B5563' },
};

const toMin = (hhmm?: string): number => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
};
const fmtHour = (min: number): string => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

export const ReservationTimeline: React.FC<Props> = ({ reservations, tables, settings, selectedDate, onViewReservation }) => {
  const openMin = toMin(settings?.openingTime || '10:00');
  const closeMinRaw = toMin(settings?.closingTime || settings?.lastReservationTime || '23:00');
  // Extend the axis so the last sitting's block isn't clipped.
  const closeMin = Math.max(closeMinRaw + (settings?.defaultDurationMinutes || 90), openMin + 60);
  const totalMin = closeMin - openMin;
  const trackW = totalMin * PX_PER_MIN;

  // Hour ticks across the axis.
  const ticks = useMemo(() => {
    const out: number[] = [];
    const first = Math.ceil(openMin / 60) * 60;
    for (let m = first; m <= closeMin; m += 60) out.push(m);
    return out;
  }, [openMin, closeMin]);

  // Reservations that occupy a given table (single or as part of a group).
  const byTable = useMemo(() => {
    const map = new Map<number, Reservation[]>();
    for (const t of tables) map.set(t.id, []);
    for (const r of reservations) {
      if (r.status === 'CANCELLED') continue;
      const ids = new Set<number>();
      if (r.tableId != null) ids.add(r.tableId);
      (r.tableIds || []).forEach((id) => ids.add(id));
      ids.forEach((id) => { if (map.has(id)) map.get(id)!.push(r); });
    }
    return map;
  }, [reservations, tables]);

  // "Now" marker when viewing today.
  const nowMin = useMemo(() => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (iso !== selectedDate) return null;
    const m = today.getHours() * 60 + today.getMinutes();
    return m >= openMin && m <= closeMin ? m : null;
  }, [selectedDate, openMin, closeMin]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Scroll so "now" (or opening) sits a little in from the left.
    if (scrollRef.current && nowMin != null) {
      scrollRef.current.scrollLeft = Math.max(0, (nowMin - openMin) * PX_PER_MIN - 120);
    }
  }, [nowMin, openMin]);

  if (tables.length === 0) {
    return <div className="content-card p-6 text-center" style={{ color: 'var(--text-tertiary)' }}>No reservable tables to show on the timeline.</div>;
  }

  return (
    <div className="content-card" style={{ overflow: 'hidden' }}>
      <div ref={scrollRef} style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: LABEL_W + trackW }}>
          {/* Hour header */}
          <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-surface, #fff)' }}>
            <div style={{ width: LABEL_W, flexShrink: 0, borderBottom: '1px solid var(--border-subtle, #E3DCCF)' }} />
            <div style={{ position: 'relative', height: 28, width: trackW, borderBottom: '1px solid var(--border-subtle, #E3DCCF)' }}>
              {ticks.map((m) => (
                <div key={m} style={{ position: 'absolute', left: (m - openMin) * PX_PER_MIN, top: 0, height: '100%', borderLeft: '1px solid var(--border-subtle, #Eee)', paddingLeft: 4, fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtHour(m)}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {tables.map((t) => {
            const items = byTable.get(t.id) || [];
            return (
              <div key={t.id} style={{ display: 'flex', height: ROW_H, borderBottom: '1px solid var(--border-subtle, #F0EBE1)' }}>
                <div style={{ width: LABEL_W, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 8px', borderRight: '1px solid var(--border-subtle, #E3DCCF)', background: 'var(--bg-surface, #fff)', position: 'sticky', left: 0, zIndex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t.seatingCapacity} seats</span>
                </div>
                <div style={{ position: 'relative', width: trackW, background: 'var(--bg-sunken, #FAF8F4)' }}>
                  {/* hour gridlines */}
                  {ticks.map((m) => (
                    <div key={m} style={{ position: 'absolute', left: (m - openMin) * PX_PER_MIN, top: 0, height: '100%', borderLeft: '1px solid var(--border-subtle, #F0EBE1)' }} />
                  ))}
                  {nowMin != null && (
                    <div style={{ position: 'absolute', left: (nowMin - openMin) * PX_PER_MIN, top: 0, height: '100%', width: 2, background: '#B3261E', zIndex: 3 }} />
                  )}
                  {items.map((r) => {
                    const s = Math.max(openMin, toMin(r.startTime));
                    const e = Math.min(closeMin, toMin(r.endTime));
                    const left = (s - openMin) * PX_PER_MIN;
                    const width = Math.max(28, (e - s) * PX_PER_MIN - 2);
                    const c = STATUS_COLOR[r.status] || STATUS_COLOR.PENDING;
                    return (
                      <button
                        key={`${r.id}-${t.id}`}
                        onClick={() => onViewReservation(r)}
                        title={`${r.customerName} · party ${r.partySize} · ${r.startTime}–${r.endTime} · ${r.status}`}
                        className="sog-press"
                        style={{
                          position: 'absolute', left, top: 5, height: ROW_H - 12, width,
                          background: c.bg, color: c.text, border: 'none', borderRadius: 6,
                          padding: '0 6px', textAlign: 'left', overflow: 'hidden', cursor: 'pointer',
                          fontSize: 11, fontWeight: 600, lineHeight: 1.1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                        }}
                      >
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customerName}</span>
                        <span style={{ fontWeight: 500, opacity: 0.9, whiteSpace: 'nowrap' }}>{r.partySize}p · {r.startTime}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '8px 12px', borderTop: '1px solid var(--border-subtle, #E3DCCF)' }}>
        {(['CONFIRMED', 'SEATED', 'PENDING', 'COMPLETED', 'NO_SHOW'] as const).map((st) => (
          <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-secondary)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[st].bg }} />
            {st.charAt(0) + st.slice(1).toLowerCase().replace('_', '-')}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ReservationTimeline;
